import { useEffect, useRef, useCallback } from 'react';
import {
  LINE_TYPE_CONFIG,
  LINE_STROKE_WEIGHT,
  LINE_STROKE_OPACITY,
  SNAP_THRESHOLD_PX,
  type LineType,
} from './lineDrawingConstants';

interface LineCompleteData {
  type: LineType;
  coordinates: Array<{ lat: number; lng: number }>;
  lengthFt: number;
}

interface UseLineDrawingParams {
  map: google.maps.Map | null;
  activeLineType: LineType | null;
  isActive: boolean;
  polygonVertices: Array<{ lat: number; lng: number }>;
  existingLineEndpoints: Array<{ lat: number; lng: number }>;
  onLineComplete: (data: LineCompleteData) => void;
  snapEnabled?: boolean;
}

export function useLineDrawing({
  map,
  activeLineType,
  isActive,
  polygonVertices,
  existingLineEndpoints,
  onLineComplete,
  snapEnabled = true,
}: UseLineDrawingParams) {
  const currentPointsRef = useRef<google.maps.LatLng[]>([]);
  const previewPolylineRef = useRef<google.maps.Polyline | null>(null);
  const confirmedPolylineRef = useRef<google.maps.Polyline | null>(null);
  const snapMarkerRef = useRef<google.maps.Marker | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const moveListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dblClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const rightClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const keyListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const activeLineTypeRef = useRef(activeLineType);
  const onLineCompleteRef = useRef(onLineComplete);
  const snapEnabledRef = useRef(snapEnabled);

  // Keep refs up to date
  activeLineTypeRef.current = activeLineType;
  onLineCompleteRef.current = onLineComplete;
  snapEnabledRef.current = snapEnabled;

  // Build snap targets from polygon vertices + existing line endpoints
  const snapTargetsRef = useRef<Array<{ lat: number; lng: number }>>([]);
  useEffect(() => {
    snapTargetsRef.current = [...polygonVertices, ...existingLineEndpoints];
  }, [polygonVertices, existingLineEndpoints]);

  const getColor = useCallback((): string => {
    return activeLineTypeRef.current
      ? LINE_TYPE_CONFIG[activeLineTypeRef.current]?.color || '#FFFFFF'
      : '#FFFFFF';
  }, []);

  const findSnapTarget = useCallback(
    (latLng: google.maps.LatLng): google.maps.LatLng | null => {
      if (!map || !snapEnabledRef.current) return null;

      const projection = map.getProjection();
      if (!projection) return null;

      const zoom = map.getZoom() || 20;
      const scale = Math.pow(2, zoom);

      const cursorPoint = projection.fromLatLngToPoint(latLng);
      if (!cursorPoint) return null;

      let closestTarget: google.maps.LatLng | null = null;
      let closestDistPx = Infinity;

      for (const target of snapTargetsRef.current) {
        const targetLatLng = new google.maps.LatLng(target.lat, target.lng);
        const targetPoint = projection.fromLatLngToPoint(targetLatLng);
        if (!targetPoint) continue;

        const dx = (cursorPoint.x - targetPoint.x) * scale;
        const dy = (cursorPoint.y - targetPoint.y) * scale;
        const distPx = Math.sqrt(dx * dx + dy * dy);

        if (distPx < closestDistPx && distPx < SNAP_THRESHOLD_PX) {
          closestDistPx = distPx;
          closestTarget = targetLatLng;
        }
      }

      return closestTarget;
    },
    [map]
  );

  const calculateLengthFt = useCallback((points: google.maps.LatLng[]): number => {
    if (points.length < 2) return 0;
    let totalMeters = 0;
    for (let i = 0; i < points.length - 1; i++) {
      totalMeters += google.maps.geometry.spherical.computeDistanceBetween(
        points[i],
        points[i + 1]
      );
    }
    return totalMeters * 3.28084;
  }, []);

  const clearWorkingState = useCallback(() => {
    currentPointsRef.current = [];

    if (previewPolylineRef.current) {
      previewPolylineRef.current.setMap(null);
      previewPolylineRef.current = null;
    }
    if (confirmedPolylineRef.current) {
      confirmedPolylineRef.current.setMap(null);
      confirmedPolylineRef.current = null;
    }
    if (snapMarkerRef.current) {
      snapMarkerRef.current.setMap(null);
      snapMarkerRef.current = null;
    }
  }, []);

  const finalizeLine = useCallback(() => {
    const points = currentPointsRef.current;
    if (points.length < 2 || !activeLineTypeRef.current) {
      clearWorkingState();
      return;
    }

    const coordinates = points.map((p) => ({ lat: p.lat(), lng: p.lng() }));
    const lengthFt = calculateLengthFt(points);

    onLineCompleteRef.current({
      type: activeLineTypeRef.current,
      coordinates,
      lengthFt: Math.round(lengthFt * 10) / 10,
    });

    clearWorkingState();
  }, [calculateLengthFt, clearWorkingState]);

  // Attach / detach map listeners
  useEffect(() => {
    if (!map || !isActive || !activeLineType) {
      // Clean up if deactivating
      if (currentPointsRef.current.length >= 2) {
        finalizeLine();
      } else {
        clearWorkingState();
      }
      // Remove listeners
      clickListenerRef.current?.remove();
      moveListenerRef.current?.remove();
      dblClickListenerRef.current?.remove();
      rightClickListenerRef.current?.remove();
      if (keyListenerRef.current) {
        document.removeEventListener('keydown', keyListenerRef.current);
        keyListenerRef.current = null;
      }
      if (map) {
        map.setOptions({ draggableCursor: '' });
      }
      return;
    }

    // Set crosshair cursor
    map.setOptions({ draggableCursor: 'crosshair' });

    // Click handler: add point
    clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || !activeLineTypeRef.current) return;

      const snapTarget = findSnapTarget(e.latLng);
      const point = snapTarget || e.latLng;

      currentPointsRef.current.push(point);

      // Update confirmed polyline
      if (!confirmedPolylineRef.current) {
        confirmedPolylineRef.current = new google.maps.Polyline({
          path: [point],
          strokeColor: getColor(),
          strokeWeight: LINE_STROKE_WEIGHT,
          strokeOpacity: LINE_STROKE_OPACITY,
          map,
          clickable: false,
        });
      } else {
        confirmedPolylineRef.current.getPath().push(point);
      }
    });

    // Mousemove handler: preview line + snap detection
    moveListenerRef.current = map.addListener(
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        const snapTarget = findSnapTarget(e.latLng);
        const cursorPos = snapTarget || e.latLng;

        // Show/hide snap marker
        if (snapTarget) {
          if (!snapMarkerRef.current) {
            snapMarkerRef.current = new google.maps.Marker({
              position: snapTarget,
              map,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#FFFFFF',
                fillOpacity: 0.8,
                strokeColor: getColor(),
                strokeWeight: 2,
              },
              clickable: false,
              zIndex: 999,
            });
          } else {
            snapMarkerRef.current.setPosition(snapTarget);
            snapMarkerRef.current.setMap(map);
          }
        } else if (snapMarkerRef.current) {
          snapMarkerRef.current.setMap(null);
        }

        // Preview polyline from last point to cursor
        if (currentPointsRef.current.length > 0) {
          const lastPoint =
            currentPointsRef.current[currentPointsRef.current.length - 1];

          if (!previewPolylineRef.current) {
            previewPolylineRef.current = new google.maps.Polyline({
              path: [lastPoint, cursorPos],
              strokeColor: getColor(),
              strokeWeight: LINE_STROKE_WEIGHT,
              strokeOpacity: 0.5,
              map,
              clickable: false,
              icons: [
                {
                  icon: {
                    path: 'M 0,-0.5 0,0.5',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    scale: 3,
                  },
                  offset: '0',
                  repeat: '10px',
                },
              ],
            });
          } else {
            previewPolylineRef.current.setPath([lastPoint, cursorPos]);
          }
        }
      }
    );

    // Double-click handler: finalize line
    dblClickListenerRef.current = map.addListener('dblclick', (e: google.maps.MapMouseEvent) => {
      e.stop?.();
      // The click event fires before dblclick, so we already have the point added.
      // Remove the duplicate last point that was added by the single-click
      if (currentPointsRef.current.length > 2) {
        currentPointsRef.current.pop();
        confirmedPolylineRef.current?.getPath().pop();
      }
      finalizeLine();
    });

    // Right-click handler: finalize line
    rightClickListenerRef.current = map.addListener(
      'rightclick',
      () => {
        finalizeLine();
      }
    );

    // Keyboard handler
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearWorkingState();
      }
    };
    keyListenerRef.current = keyHandler;
    document.addEventListener('keydown', keyHandler);

    return () => {
      clickListenerRef.current?.remove();
      moveListenerRef.current?.remove();
      dblClickListenerRef.current?.remove();
      rightClickListenerRef.current?.remove();
      if (keyListenerRef.current) {
        document.removeEventListener('keydown', keyListenerRef.current);
        keyListenerRef.current = null;
      }
      map.setOptions({ draggableCursor: '' });
    };
  }, [map, isActive, activeLineType, findSnapTarget, getColor, finalizeLine, clearWorkingState]);

  // When activeLineType changes, finalize any in-progress line and update color
  useEffect(() => {
    if (confirmedPolylineRef.current && currentPointsRef.current.length >= 2) {
      finalizeLine();
    } else {
      clearWorkingState();
    }
    // Update polyline color if needed
    if (confirmedPolylineRef.current) {
      confirmedPolylineRef.current.setOptions({ strokeColor: getColor() });
    }
    if (previewPolylineRef.current) {
      previewPolylineRef.current.setOptions({ strokeColor: getColor() });
    }
  }, [activeLineType]);

  return {
    cancelCurrentLine: clearWorkingState,
    hasLineInProgress: () => currentPointsRef.current.length > 0,
  };
}
